import { Dimensions, TextStyle } from 'react-native';
import { colors } from './colors';
import { typeScale } from './responsive';

/**
 * Homingo type system.
 *
 * Per the typography board: **Playfair Display** (high-contrast serif) for
 * display headings, **Satoshi** (neutral grotesque) for everything else.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY VARIANT NAMES A WEIGHT-SPECIFIC FAMILY
 * ---------------------------------------------------------------------------
 * With a custom font, `fontFamily: 'Satoshi' + fontWeight: '700'` works on iOS
 * and SILENTLY FAILS ON ANDROID - Android resolves a bundled font strictly by
 * file name and ignores `fontWeight`, so every weight renders as Regular. The
 * bug does not appear on the iOS simulator, which is exactly how it survives
 * to release.
 *
 * The fix, applied throughout: name the concrete face (`Satoshi-Bold`).
 *
 * ---------------------------------------------------------------------------
 * FONT FILES ARE NOT IN THE PROJECT YET
 * ---------------------------------------------------------------------------
 * assets/fonts does not exist. Until it does, every family name here resolves
 * to nothing and both platforms fall back to their system font. Nothing
 * crashes - the app just renders in Roboto / SF.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY VARIANT ALSO SETS A NUMERIC WEIGHT
 * ---------------------------------------------------------------------------
 * Because of the line above. With no Satoshi to load, `fontFamily:
 * 'Satoshi-Bold'` is the only thing saying "this is bold" - and it says it to
 * a font that is not there. The system fallback has no idea it was meant to be
 * heavy, so every heading in the app rendered at Regular: section titles no
 * darker than the rows beneath them, and a screen with no visible hierarchy.
 *
 * Six files had already worked around this one heading at a time. Putting the
 * weight in the ramp fixes all of them at once and stops the next variant from
 * forgetting.
 *
 * It stays correct once the fonts land. Android resolves a bundled font
 * strictly by file name and ignores `fontWeight` outright; iOS gets a weight
 * that already agrees with the face it names, so there is nothing to
 * synthesise. What must never happen is the reverse - a REGULAR family paired
 * with a bold `fontWeight`, which iOS would fake and Android would ignore,
 * giving two different screens on two platforms.
 */

/** Concrete font faces. Each must match a real file in assets/fonts/. */
export const fonts = {
  displayRegular: 'PlayfairDisplay-Regular',
  displayMedium: 'PlayfairDisplay-Medium',
  displaySemiBold: 'PlayfairDisplay-SemiBold',
  displayBold: 'PlayfairDisplay-Bold',

  bodyRegular: 'Satoshi-Regular',
  bodyMedium: 'Satoshi-Medium',
  bodyBold: 'Satoshi-Bold',
} as const;

/**
 * ---------------------------------------------------------------------------
 * THE RAMP MOVES WITH THE DEVICE - A LITTLE
 * ---------------------------------------------------------------------------
 * Every size and line height below is a designed value for a 390pt phone, run
 * through `pt()` on the way out. On any phone between 360 and 600pt wide that
 * multiplier is exactly 1 and nothing changes; below 360 the whole ramp steps
 * down ~10% so a 40pt headline stops wrapping to three lines on a 320pt
 * screen, and on a tablet it steps up 8% so body copy is not a thread.
 *
 * Read once from `Dimensions` rather than a hook, because `StyleSheet.create`
 * at the bottom of this file runs at import and cannot wait for a render. The
 * cost of that is a device which changes width mid-session - a fold opening,
 * a split-screen drag - keeps the ramp it started with until the app is
 * relaunched. Layout follows the fold immediately (that side is all hooks); it
 * is only the ~8% type step that lags, which is a fair trade for not making
 * every `Text` in the app subscribe to dimension changes.
 */
const TYPE_SCALE = typeScale(Dimensions.get('window').width);

/** A designed point size, on this device's ramp. */
const pt = (size: number) => Math.round(size * TYPE_SCALE);

/**
 * Font size ramp, in points.
 *
 * The top two steps are larger than they were (30/34). A page headline is the
 * one piece of type that has a whole screen to itself and nothing to compete
 * with, and at 34 "My Bookings" sat on a full-width illustration reading like
 * a section title that happened to be over a picture.
 */
export const fontSizes = {
  xs: pt(11),
  sm: pt(13),
  md: pt(15),
  lg: pt(17),
  xl: pt(20),
  display: pt(34),
  displayLg: pt(40),
} as const;

/**
 * Numeric weights.
 *
 * Every variant below now carries the one that matches its named face, and
 * that is deliberate - see `WHY EVERY VARIANT ALSO SETS A NUMERIC WEIGHT`.
 */
export const fontWeights = {
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const satisfies Record<string, TextStyle['fontWeight']>;

/**
 * Ready-made text styles - the vocabulary the UI is written in.
 * `<Text variant="display">` rather than ad-hoc fontSize/fontFamily pairs.
 *
 * `lineHeight` is set explicitly on every variant on purpose: left unset,
 * Android and iOS compute different defaults from the font's own metrics and
 * the same screen ends up with different vertical rhythm on each platform.
 */
export const textVariants = {
  /**
   * Hero headline - "My Bookings", "One App. All Home Services."
   *
   * Bold, not regular. A high-contrast serif at Regular is a magazine standfirst;
   * these headlines are the name of the page, and they are set over artwork that
   * a light weight simply loses against.
   */
  display: {
    fontFamily: fonts.displayBold,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.displayLg,
    lineHeight: pt(46),
    letterSpacing: -0.6,
  },
  /** Secondary hero, one step down. */
  displaySm: {
    fontFamily: fonts.displaySemiBold,
    fontWeight: fontWeights.semibold,
    fontSize: fontSizes.display,
    /*
     * 44, not 40.
     *
     * 34pt type in a 40pt line is a ratio of 1.18, and this face needs about
     * 1.28 to fit an ascender and a descender in the box. Every other place
     * this variant is used renders digits and a rupee sign - none of which
     * descend - so the shortfall only showed once a word with a "g" in it
     * was set in it, and then it clipped the tail clean off.
     */
    lineHeight: pt(44),
    letterSpacing: -0.4,
  },
  /**
   * Large bold sans, between `title` and `displaySm`.
   *
   * Fills the gap in the ramp: a promo banner headline needs to dominate its
   * card without being a page-level hero, and at `displaySm` it wrapped to
   * three lines and overflowed. Bold sans rather than the display serif
   * because these sit on small, dense surfaces.
   */
  titleLg: {
    fontFamily: fonts.bodyBold,
    fontWeight: fontWeights.bold,
    fontSize: pt(24),
    lineHeight: pt(30),
    letterSpacing: -0.3,
  },
  /** Section titles - "Book again", "Your Bookings". */
  title: {
    fontFamily: fonts.bodyBold,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.xl,
    lineHeight: pt(26),
    letterSpacing: -0.2,
  },
  /** Card / row titles - "Cleaning". */
  subtitle: {
    fontFamily: fonts.bodyMedium,
    fontWeight: fontWeights.semibold,
    fontSize: fontSizes.md,
    lineHeight: pt(20),
  },
  /** Default body copy. */
  body: {
    fontFamily: fonts.bodyRegular,
    fontWeight: fontWeights.regular,
    fontSize: fontSizes.md,
    lineHeight: pt(22),
  },
  /** Supporting copy - "Verified pros. Quality service." */
  bodySm: {
    fontFamily: fonts.bodyRegular,
    fontWeight: fontWeights.regular,
    fontSize: fontSizes.sm,
    lineHeight: pt(19),
  },
  /** Button text, tile labels. */
  label: {
    fontFamily: fonts.bodyMedium,
    fontWeight: fontWeights.semibold,
    fontSize: fontSizes.md,
    lineHeight: pt(20),
  },
  /**
   * Bold at body size.
   *
   * The ramp had bold only at 22 (`titleLg`), 17 (`title`) and 11 (`eyebrow`),
   * so emphasising a word inside running text meant jumping a size as well as
   * a weight. This is the same size as `label`, heavier.
   */
  labelBold: {
    fontFamily: fonts.bodyBold,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.md,
    lineHeight: pt(20),
  },
  /** Small labels under icons, chip text, button labels. */
  labelSm: {
    fontFamily: fonts.bodyBold,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.sm,
    lineHeight: pt(17),
  },
  /** Timestamps, captions. */
  caption: {
    fontFamily: fonts.bodyRegular,
    fontWeight: fontWeights.regular,
    fontSize: fontSizes.sm,
    lineHeight: pt(18),
  },
  /** Uppercase eyebrow - "STEP 1 OF 3". */
  eyebrow: {
    fontFamily: fonts.bodyBold,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.xs,
    lineHeight: pt(14),
    letterSpacing: 1.1,
    textTransform: 'uppercase',
  },
} as const satisfies Record<string, TextStyle>;

export type TextVariant = keyof typeof textVariants;

/**
 * Type for a `TextInput`.
 *
 * `TextInput` cannot take a `Text` variant - it is not a `Text` - so every
 * field in the app was carrying its own `fontSize`, and they had drifted to
 * three different values. This is the one place that decides what typed text
 * looks like.
 *
 * `padding: 0` because Android gives inputs a default inner padding that no
 * other control has, which throws a field's height off its intended box.
 */
export const inputText = {
  fontFamily: fonts.bodyMedium,
  fontWeight: fontWeights.medium,
  fontSize: fontSizes.md,
  color: colors.ink,
  padding: 0,
} as const;
